import {
  ReactElement,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react'
import { fetchMissionCAST, toggleMissionCast } from './MissionCASTSlice'
import { useMap } from 'react-leaflet'
import { useDispatch, useSelector } from 'react-redux'
import L, { LatLngExpression } from 'leaflet'
import { createRoot, Root } from 'react-dom/client'
import { deselectMenuItemById } from '../../menus/SideNavigation/SideNavigationSlice'
import { RootState } from '../../store'
import useErrorStatus from '../../hooks/UseErrorStatus'
import { ErrorTimeouts } from '../../constants/ErrorTimeouts'
import MissionCastTable from './MissionCastTable'
import { MissioncastRow } from '../../interfaces/missioncast'
import Spinner from '../Spinner/Spinner'

export const MissionCAST = function (): ReactElement {
  const defaultSpinnerElement = useMemo(
    () => <Spinner message="Your custom analytics is being generated" />,
    []
  )

  const [content, setContent] = useState<Record<string, MissioncastRow> | null>(
    null
  )
  const [coordinates, setCoordinates] = useState<Array<number | null>>([
    null,
    null,
  ])
  const [popup, setPopup] = useState<L.Popup | null>(null)

  const location = useSelector((state: RootState) => state.user.defaultLocation)
  const selectedItemId = useSelector(
    (state: RootState) => state.sideNav.selectedItems
  )
  const dispatch = useDispatch()
  const map = useMap()
  const errorStatus = useErrorStatus()

  const controllerRef = useRef(new AbortController())
  const exportRef = useRef<HTMLDivElement | null>(null)
  const popupRootRef = useRef<Root | null>(null)
  const popupContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const controller = controllerRef.current
    const signal = controller.signal

    dispatch(
      fetchMissionCAST({
        lat: location.latitude,
        long: location.longitude,
        signal,
        controller,
      })
    )
      .unwrap()
      .then((response) => {
        setContent(response.features[0].properties)
        setCoordinates(response.features[0].geometry.coordinates)
      })
      .catch((error) => {
        errorStatus.addMessage(
          'MissionCAST: ' + error.message,
          400,
          'error',
          false,
          ErrorTimeouts.MISSIONCAST
        )
        map.closePopup()
      })

    return () => {
      controller.abort()
      map.closePopup()
    }
  }, [map])

  const makeDraggable = useCallback(
    (popup: L.Popup) => {
      const pos = map.latLngToLayerPoint(popup.getLatLng() as LatLngExpression)
      L.DomUtil.setPosition(popup._wrapper.parentNode, pos)
      const draggable = new L.Draggable(popup._container, popup._wrapper)
      draggable.enable()
    },
    [map]
  )

  useEffect(() => {
    const newPopup = new L.Popup({
      minWidth: 300,
      maxWidth: window.innerWidth * 0.9,
    })
      .setLatLng([location.latitude, location.longitude])
      .openOn(map)
      .on('remove', () => {
        dispatch(toggleMissionCast(false))
        if (selectedItemId.includes('decision-missioncast')) {
          dispatch(deselectMenuItemById('decision-missioncast'))
        }
      })

    setPopup(newPopup)
    makeDraggable(newPopup)

    const container = document.createElement('div')
    popupContainerRef.current = container
    popupRootRef.current = createRoot(container)
    newPopup.setContent(container)

    // Content (e.g. spinner -> table, or table tab switches) changes the
    // container's natural size without calling setContent again, so Leaflet
    // won't know to resize the popup unless we nudge it via update().
    const resizeObserver = new ResizeObserver(() => newPopup.update())
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
      popupRootRef.current?.unmount()
      newPopup.removeFrom(map)
      setPopup(null)
    }
  }, [map, selectedItemId])

  useEffect(() => {
    if (!popupRootRef.current) return

    popupRootRef.current.render(
      content ? (
        <MissionCastTable
          data={content}
          coordinates={coordinates}
          exportRef={exportRef}
        />
      ) : (
        defaultSpinnerElement
      )
    )
  }, [content, coordinates, defaultSpinnerElement])

  return null
}
